import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import queryString, { ParsedQuery } from 'query-string';

import { useQueryParams } from '@app/hooks';
import { generateRouteParams } from '@app/utils';
import { Route, SortDirection } from '@app/constants';

import { SearchState } from '../constants';
import { getURLState } from '../utils';

const parseSortState = (sort: string) => {
  let sortField = sort;
  let sortDirection = SortDirection.asc;

  if (sort.startsWith('-')) {
    sortField = sort.slice(1);
    sortDirection = SortDirection.desc;
  }

  return {
    sortField,
    sortDirection,
  };
};

export const useUrlManager = () => {
  const navigate = useNavigate();
  const queryParams = useQueryParams();

  const updateUrl = useCallback(
    (searchState: Partial<SearchState>, pathname: string = Route.search) => {
      navigate(
        generateRouteParams({
          pathname,
          query: getURLState(searchState),
        })
      );
    },
    [navigate]
  );

  const getSearchStateFromUrl = useCallback(
    (customQuery: ParsedQuery = queryParams): SearchState => {
      return {
        ...customQuery,
        ...(customQuery.sort && parseSortState(customQuery.sort as string)),
        ...(customQuery.facets && {
          facets: queryString.parse(customQuery.facets as string, {
            arrayFormat: 'bracket-separator',
          }),
        }),
        ...(customQuery.publicationYear && {
          publicationYear: queryString.parse(
            customQuery.publicationYear as string
          ),
        }),
      } as unknown as SearchState;
    },
    [queryParams]
  );

  return {
    updateUrl,
    getSearchStateFromUrl,
  };
};
